import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Ban, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

interface ClosureRequestModalProps {
  productName: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

const ClosureRequestModal: React.FC<ClosureRequestModalProps> = ({
  productName,
  onClose,
  onSubmit
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      toast.error('Lütfen kapatma nedenini belirtin');
      return;
    }

    try {
      setLoading(true);
      await onSubmit(reason.trim());
    } catch (error) {
      toast.error('Talep gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Ban className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Ürün Kartı Kapatma Talebi</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{productName}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Kapatma Nedeni *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                rows={4}
                placeholder="Ürün kartının kapatılma nedenini detaylı olarak açıklayın..."
                className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Bu talep ERP ekibine iletilecektir.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Dikkat!</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Kapatma talebi onaylandığında ürün kartı pasife alınacaktır. Bu işlem ERP ekibi tarafından değerlendirilecektir.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={loading || !reason.trim()}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Ban className="h-4 w-4 mr-2" />
                    Talep Gönder
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClosureRequestModal;
